/**
 * PanoManager — State container for the interior panorama subsystem.
 *
 * Responsibilities:
 * - Track active/inactive state and the current pano URL
 * - Prevent no-op transitions (same URL → no state change, no onChange)
 * - Notify the R3F PanoComponent via `onChange` when state changes
 *
 * This manager is intentionally thin — it contains ZERO Three.js or R3F code.
 * All rendering, fade transitions, texture loading, and camera interaction
 * live in `PanoComponent.tsx`.
 *
 * @see docs/ims-engine-api/12-feature-pano-measures.md
 * @see docs/ims-engine-api/02-api-contract.md — showPano / switchPano / hidePano / getPanoState
 */

import type { PanoState, PanoChangedEvent } from '../api/types/pano';

// ---------------------------------------------------------------------------
// PanoManager
// ---------------------------------------------------------------------------

export class PanoManager {
  private _active = false;
  private _url: string | null = null;

  /**
   * Fired whenever `active` or `url` changes.
   * Wired to PanoComponent.onChange by createEngine.
   */
  onChange?: (state: PanoState) => void;

  /**
   * Fired whenever `active` or `url` changes — used by createEngine to
   * emit the public `panoChanged` event.
   */
  onChanged?: (event: PanoChangedEvent) => void;

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Activate pano mode with the given URL.
   * If already active with the same URL, this is a no-op.
   * If already active with a different URL, the URL is updated (switchTo behaviour).
   */
  show(url: string): void {
    if (this._active && this._url === url) return; // no-op
    this._active = true;
    this._url = url;
    this._notify();
  }

  /**
   * Switch to a different URL while remaining in pano mode.
   * No-op if the URL is already the active one.
   */
  switchTo(url: string): void {
    if (!this._active) {
      // Not yet active — treat like show()
      this.show(url);
      return;
    }
    if (this._url === url) return; // already showing this URL
    this._url = url;
    this._notify();
  }

  /**
   * Deactivate pano mode.
   * No-op if already inactive.
   */
  hide(): void {
    if (!this._active) return;
    this._active = false;
    this._url = null;
    this._notify();
  }

  /** Get a serialisable snapshot of the current pano state. */
  getState(): PanoState {
    return { active: this._active, url: this._url };
  }

  /** Release all references. Safe to call multiple times. */
  dispose(): void {
    this._active = false;
    this._url = null;
    this.onChange = undefined;
    this.onChanged = undefined;
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  private _notify(): void {
    const state = this.getState();
    this.onChange?.(state);
    this.onChanged?.(state);
  }
}
